import {
    getDateTimeAsMoment,
    getFormattedDateTime,
} from '@eventbrite/datetime';
import { Trending as TrendingSvg } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import { $FixMe } from '@eventbrite/ts-utils';
import has from 'lodash/has';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import take from 'lodash/take';
import React from 'react';
import { formatUrl } from 'url-lib';
import { EVENTS_TYPE, EVENT_TYPE } from '../../constants';
import { EVENT_AUTOCOMPLETE_AFFILIATE } from '../../constants/constants';
import { EventSuggestion, SuggestionsState } from '../../types';
import { getTransformedContent } from '../../utils/transformation';
import { getLocationInfo } from '../../utils/venue';

const _parseEventsSuggestions = (suggestions: EventSuggestion[]) => {
    let eventSuggestions = map(
        suggestions,
        (
            {
                id,
                name,
                startDate,
                startTime,
                timezone,
                primaryVenue,
                url,
                ticketsBy,
                image,
            },
            index: number,
        ) => ({
            ticketsBy,
            value: `${EVENT_TYPE}/${id}`,
            content: name,
            imageUrl: image ? image.url : '',
            imageAlt: name,
            secondaryContent: getFormattedDateTime(
                getDateTimeAsMoment(startDate, startTime),
                false,
                timezone,
            ),
            tertiaryContent: getLocationInfo(
                primaryVenue ? primaryVenue.name : '',
                primaryVenue ? primaryVenue.address.localizedAreaDisplay : '',
            ),
            header: index === 0 ? gettext('Events') : null,
            showDivider: suggestions.length === index + 1 || index === 4,
            dividerVertSpacing: 2,
            url: formatUrl(url, { aff: EVENT_AUTOCOMPLETE_AFFILIATE }),
            isSquareImage: true,
        }),
    );

    eventSuggestions = take(eventSuggestions, 5);

    return eventSuggestions;
};

const _parseQuerySuggestions = (suggestions: $FixMe) => {
    let querySuggestions = map(suggestions, (completion, index: number) => ({
        value: completion,
        content: completion,
        iconType: <TrendingSvg />,
        header: index === 0 ? gettext('Popular') : null,
        showDivider: suggestions.length === index + 1 || index === 4,
        dividerVertSpacing: 2,
    }));

    querySuggestions = take(querySuggestions, 5);

    return querySuggestions;
};

const PARSE_MAP: { [key: string]: any } = {
    [EVENTS_TYPE]: _parseEventsSuggestions,
    query: _parseQuerySuggestions,
    event: _parseEventsSuggestions,
    tags: (tags: string[]) => tags,
    filters: (filters: string[]) => filters,
};

export const parseSuggestions = (response = {}): SuggestionsState => {
    const { result } = getTransformedContent(
        'aKeyThatDoesNotMatterInThisContext',
        {
            response,
        },
    );
    const parsedResponse = reduce(
        result.response,
        (memo, value, key) => {
            if (key in PARSE_MAP) {
                const data = PARSE_MAP[key](value);

                // if we're parsing events, we need the unparsed version as well
                if (key === EVENTS_TYPE) {
                    const rawEvents = reduce(
                        value,
                        (acc, val) => ({
                            ...acc,
                            [val.id]: val,
                        }),
                        {},
                    );

                    return {
                        ...memo,
                        rawEvents,
                        [key]: data,
                    };
                }

                return {
                    ...memo,
                    [key]: data,
                };
            }

            return memo;
        },
        {},
    );

    return {
        ...parsedResponse,
    };
};

export const transformSearchIntoSuggestions = (
    response = {},
): SuggestionsState => {
    const { result } = getTransformedContent(
        'aKeyThatDoesNotMatterInThisContext',
        {
            response,
        },
    );

    let events = [];
    let tags = [];
    let filters = [];

    if (has(result, 'response.events.results')) {
        events = result.response.events.results;
    }

    if (
        has(result, 'response.events.aggs.organizertagsautocompleteAgg.buckets')
    ) {
        const aggregatedTags =
            result.response.events.aggs.organizertagsautocompleteAgg.buckets;

        const filteredTags = aggregatedTags.filter(
            ({ docCount }: { docCount: number }) => docCount > 5,
        );

        tags = filteredTags.map(
            (tag: { key: string; docCount: number; displayName: string }) => ({
                value: tag.key,
                count: tag.docCount,
                display: tag.displayName,
            }),
        );
    }

    if (has(result, 'response.events.aggs.tags.buckets')) {
        const aggregatedFilters = result.response.events.aggs.tags.buckets;
        const filteredFilters = aggregatedFilters.filter(
            ({ docCount, key }: { docCount: number; key: string }) =>
                docCount > 5 && !key.includes('Sub'),
        );
        filters = filteredFilters.map(
            (filter: { key: string; docCount: number }) => ({
                value: filter.key,
                count: filter.docCount,
                type: filter.key.includes('Category') ? 'tags' : 'format',
            }),
        );
    }

    if (has(result, 'response.events.aggs.dates.buckets')) {
        const aggregatedDates = result.response.events.aggs.dates.buckets;
        const filteredDates = aggregatedDates.filter(
            ({ docCount, key }: { docCount: number; key: string }) =>
                docCount > 5 && key === 'this_weekend',
        );
        filters = [
            ...filters,
            ...filteredDates.map((date: { key: string; docCount: number }) => ({
                value: date.key,
                count: date.docCount,
                type: 'dates',
            })),
        ];
    }

    return {
        events,
        tags,
        filters,
    };
};
